import React from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import RequirementForm from './RequirementForm';
import RequirementStatusBadge from './RequirementStatusBadge';
import TestCaseRow from './TestCaseRow';
import Pagination from './Pagination';
import { HiOutlinePlus, HiOutlineSparkles, HiOutlinePencil, HiOutlineTrash, HiOutlineClock, HiOutlineRefresh, HiOutlineMenu } from 'react-icons/hi';
import { FiUser } from 'react-icons/fi';

const TC_PAGE_SIZE = 20;

export default function RequirementItem({
  req, reqIndex, projectId, isAdmin, canEdit, openAccordion, setOpenAccordion,
  editingReq, setEditingReq, handleUpdateReq,
  setConfirmAction, setShowTCModal, setAiModal, setAuditModal,
  syncingItemId, handleSyncRequirement,
  loadProject, usersList, currentUser, filterMyTC, filterTag,
  tcPages, setTcPages, projectTags, setTagTCModal,
  editingTC, setEditingTC, handleUpdateTC,
  handleTCDragEnd, handleAssign, handleAssignReq,
  getEnvStatut, getEnvCommentaire, selectedEnv,
  setObservationModal, setCommentThread,
  handleSyncTestCase,
}) {
  const filteredTCs = req.test_cases.filter((tc) => {
    if (filterMyTC && tc.assigned_to_id !== currentUser?.id) return false;
    if (filterTag && !(tc.tags || []).some(t => t.id === filterTag)) return false;
    return true;
  });
  const currentTcPage = tcPages[req.id] || 1;
  const paginatedTCs = filteredTCs.length > TC_PAGE_SIZE
    ? filteredTCs.slice((currentTcPage - 1) * TC_PAGE_SIZE, currentTcPage * TC_PAGE_SIZE)
    : filteredTCs;

  return (
    <Draggable key={req.id} draggableId={`req-${req.id}`} index={reqIndex}>
      {(dragProvided, dragSnapshot) => (
        <div
          id={`req-${req.id}`}
          className={`accordion-item ${dragSnapshot.isDragging ? 'dragging' : ''}`}
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
        >
          <h2 className="accordion-header">
            <button className={`accordion-button ${openAccordion === req.id ? '' : 'collapsed'}`}
              onClick={() => setOpenAccordion(openAccordion === req.id ? null : req.id)} type="button">
              <button type="button" {...dragProvided.dragHandleProps} className="drag-handle me-2" title="Glisser pour reordonner" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} style={{ background: 'none', border: 'none', padding: 0, cursor: 'grab', color: 'inherit', font: 'inherit' }}>
                <HiOutlineMenu />
              </button>
              <div className="req-header">
                <span className="d-flex align-items-center gap-2">
                  <span className="req-id">{req.exg_id}</span>
                  <span>{req.exigence}</span>
                  <span className="meta-tag">{req.test_cases.length} CT</span>
                  <span className="meta-tag">{req.type_exigence}</span>
                  <RequirementStatusBadge
                    projectId={projectId}
                    requirementId={req.id}
                    statut={req.statut_validation || 'Brouillon'}
                    onUpdate={loadProject}
                    isAdmin={isAdmin}
                  />
                </span>
                <span className="req-meta me-3">
                  <span className="meta-tag">Impact: {req.impact}</span>
                  <span className="meta-tag">Prob: {req.probabilite}</span>
                  {canEdit ? (
                    <select
                      className="form-select form-select-sm d-inline-block"
                      style={{ fontSize: '0.68rem', padding: '1px 4px', width: 'auto', minWidth: '90px' }}
                      value={req.assigned_to_id || ''}
                      onChange={(e) => { e.stopPropagation(); handleAssignReq(req.id, e.target.value ? Number.parseInt(e.target.value) : null); }}
                      onClick={(e) => e.stopPropagation()}
                      title="Assigner l'exigence"
                    >
                      <option value="">-- Assigne --</option>
                      {usersList.map((u) => (
                        <option key={u.id} value={u.id}>{u.username}</option>
                      ))}
                    </select>
                  ) : (
                    req.assigned_to_username && <span className="meta-tag"><FiUser style={{ fontSize: '0.6rem' }} /> {req.assigned_to_username}</span>
                  )}
                </span>
              </div>
            </button>
          </h2>
          <div className={`accordion-collapse collapse ${openAccordion === req.id ? 'show' : ''}`}>
            <div className="accordion-body">
              {editingReq?.id === req.id ? (
                <RequirementForm initial={editingReq} onSubmit={handleUpdateReq} onCancel={() => setEditingReq(null)} />
              ) : (
                <div className="mb-3">
                  {req.desc_exigence && <p className="small text-muted">{req.desc_exigence}</p>}
                  <div className="d-flex gap-2 mb-3">
                    {canEdit && <button className="btn btn-outline-primary btn-sm" onClick={() => setEditingReq(req)}><HiOutlinePencil /> Modifier</button>}
                    {canEdit && <button className="btn btn-outline-danger btn-sm" onClick={() => setConfirmAction({ type: 'req', id: req.id })}><HiOutlineTrash /> Supprimer</button>}
                    {canEdit && <button className="btn btn-outline-success btn-sm" onClick={() => setShowTCModal(req.id)}><HiOutlinePlus /> Cas de Test</button>}
                    {canEdit && <button className="btn btn-outline-warning btn-sm" onClick={() => setAiModal({ mode: 'enrich', requirement: req })}><HiOutlineSparkles /> Enrichir IA</button>}
                    <button className="btn btn-outline-secondary btn-sm" onClick={() => setAuditModal({ entityType: 'requirement', entityId: req.id, entityLabel: req.exg_id })}><HiOutlineClock /> Historique</button>
                    {canEdit && (
                      <button
                        className="btn btn-outline-info btn-sm"
                        disabled={!!syncingItemId}
                        onClick={() => handleSyncRequirement(req.id, req.exg_id)}
                        title="Synchroniser cette exigence + ses CT vers Squash TM"
                      >
                        {syncingItemId === `req-${req.id}` ? <><span className="spinner-border spinner-border-sm me-1"></span> Sync...</> : <><HiOutlineRefresh /> Sync Squash</>}
                      </button>
                    )}
                  </div>
                </div>
              )}

              <DragDropContext onDragEnd={(result) => handleTCDragEnd(req.id, result)}>
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: '30px' }}></th>
                      <th style={{ width: '80px' }}>ID</th>
                      <th>Cas de Test</th>
                      <th>Prerequis</th>
                      <th>Action</th>
                      <th>Resultat Attendu</th>
                      <th style={{ width: '70px' }}>Statut</th>
                      <th style={{ width: '100px' }}>Tags</th>
                      <th style={{ width: '120px' }}>Assigne</th>
                      <th style={{ width: '100px' }}>Actions</th>
                    </tr>
                  </thead>
                  <Droppable droppableId={`tc-${req.id}`}>
                    {(tcProvided) => (
                      <tbody ref={tcProvided.innerRef} {...tcProvided.droppableProps}>
                        {paginatedTCs.map((tc, tcIndex) => {
                          const globalIndex = filteredTCs.length > TC_PAGE_SIZE
                            ? (currentTcPage - 1) * TC_PAGE_SIZE + tcIndex
                            : tcIndex;
                          return (
                            <TestCaseRow
                              key={tc.id}
                              tc={tc}
                              globalIndex={globalIndex}
                              req={req}
                              projectId={projectId}
                              canEdit={canEdit}
                              editingTC={editingTC}
                              getEnvStatut={getEnvStatut}
                              getEnvCommentaire={getEnvCommentaire}
                              selectedEnv={selectedEnv}
                              loadProject={loadProject}
                              handleUpdateTC={handleUpdateTC}
                              setEditingTC={setEditingTC}
                              setConfirmAction={setConfirmAction}
                              setAiModal={setAiModal}
                              setAuditModal={setAuditModal}
                              setCommentThread={setCommentThread}
                              setObservationModal={setObservationModal}
                              handleAssign={handleAssign}
                              usersList={usersList}
                              projectTags={projectTags}
                              setTagTCModal={setTagTCModal}
                              syncingItemId={syncingItemId}
                              handleSyncTestCase={handleSyncTestCase}
                              filteredTCsLength={filteredTCs.length}
                              filterMyTC={filterMyTC}
                            />
                          );
                        })}
                        {tcProvided.placeholder}
                        {req.test_cases.length === 0 && (
                          <tr><td colSpan="10" className="text-center text-muted py-3" style={{ fontSize: '0.82rem' }}>Aucun cas de test</td></tr>
                        )}
                        {filterMyTC && req.test_cases.length > 0 && req.test_cases.filter((tc) => tc.assigned_to_id === currentUser?.id).length === 0 && (
                          <tr><td colSpan="10" className="text-center text-muted py-2" style={{ fontSize: '0.78rem' }}>Aucun cas de test assigne</td></tr>
                        )}
                      </tbody>
                    )}
                  </Droppable>
                </table>
                {filteredTCs.length > TC_PAGE_SIZE && (
                  <Pagination
                    currentPage={currentTcPage}
                    totalItems={filteredTCs.length}
                    pageSize={TC_PAGE_SIZE}
                    onPageChange={(page) => setTcPages((prev) => ({ ...prev, [req.id]: page }))}
                  />
                )}
              </DragDropContext>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
}

RequirementItem.propTypes = {
  req: PropTypes.object.isRequired,
  reqIndex: PropTypes.number.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isAdmin: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
  openAccordion: PropTypes.number,
  setOpenAccordion: PropTypes.func.isRequired,
  editingReq: PropTypes.object,
  setEditingReq: PropTypes.func.isRequired,
  handleUpdateReq: PropTypes.func.isRequired,
  setConfirmAction: PropTypes.func.isRequired,
  setShowTCModal: PropTypes.func.isRequired,
  setAiModal: PropTypes.func.isRequired,
  setAuditModal: PropTypes.func.isRequired,
  syncingItemId: PropTypes.string,
  handleSyncRequirement: PropTypes.func.isRequired,
  loadProject: PropTypes.func.isRequired,
  usersList: PropTypes.array.isRequired,
  currentUser: PropTypes.object,
  filterMyTC: PropTypes.bool.isRequired,
  filterTag: PropTypes.number,
  tcPages: PropTypes.object.isRequired,
  setTcPages: PropTypes.func.isRequired,
  projectTags: PropTypes.array.isRequired,
  setTagTCModal: PropTypes.func.isRequired,
  editingTC: PropTypes.object,
  setEditingTC: PropTypes.func.isRequired,
  handleUpdateTC: PropTypes.func.isRequired,
  handleTCDragEnd: PropTypes.func.isRequired,
  handleAssign: PropTypes.func.isRequired,
  handleAssignReq: PropTypes.func.isRequired,
  getEnvStatut: PropTypes.func.isRequired,
  getEnvCommentaire: PropTypes.func.isRequired,
  selectedEnv: PropTypes.string.isRequired,
  setObservationModal: PropTypes.func.isRequired,
  setCommentThread: PropTypes.func.isRequired,
  handleSyncTestCase: PropTypes.func.isRequired,
};
